export const OWNERSHIP_PERCENTAGE_WARNING = 'Ownership percentages must total 100% at each level';
export const MISSING_OWNERSHIP_PERCENTAGE_WARNING = 'The ownership percentage of a shareholder is missing';

export function missingOwnershipPercentageEdges(snapshot) {
  const nodes = Array.isArray(snapshot?.nodes) ? snapshot.nodes : [];
  const byId = new Map(nodes.map(node => [String(node?.ID || node?.id || '').trim(), node]));
  return nodes.flatMap(node => {
    const parentId = String(node?.parentIDs?.[0] || '').trim();
    const parent = byId.get(parentId);
    if (!parent) return [];
    const type = String(parentId === 'X0' ? snapshot?.initialAHType || parent.type : parent.type).trim();
    const category = String(parent.entityCategory || '').trim();
    if (parent.isPIC || category === 'pic' || type === 'PIC' || type === 'HoldingPassive' ||
      (category === 'holding' && parent.holdingKind === 'passive')) return [];
    const requiresPercent = ['Non Quoted Operational Company', 'Operating Company', 'HoldingActive', 'Holding (Active)'].includes(type) ||
      category === 'nonQuotedOperational' || (category === 'holding' && parent.holdingKind === 'active');
    if (!requiresPercent || String(node.percent ?? '').trim() !== '') return [];
    return [{ parentId, nodeId: String(node.ID || node.id || '').trim() }];
  });
}

// Use the same groups and rounding tolerance for the warning and diagram labels.
export function invalidOwnershipPercentageEdges(snapshot, parsePercent = Number) {
  const nodes = Array.isArray(snapshot?.nodes) ? snapshot.nodes : [];
  const byId = new Map(nodes.map(node => [String(node?.ID || node?.id || '').trim(), node]));
  const groups = new Map();
  nodes.forEach(node => {
    const parents = Array.isArray(node?.parentIDs) ? node.parentIDs : [];
    const parentId = String(parents[0] || '').trim();
    if (!parentId || node?.percent === '' || node?.percent == null) return;
    const percent = parsePercent(node.percent);
    if (!Number.isFinite(percent)) return;
    if (!groups.has(parentId)) groups.set(parentId, []);
    groups.get(parentId).push({ parentId, nodeId: String(node.ID || node.id || '').trim(), percent });
  });

  return [...groups.entries()].flatMap(([parentId, children]) => {
    const parent = byId.get(parentId);
    const type = String(parentId === 'X0' ? snapshot?.initialAHType || parent?.type || '' : parent?.type || '').trim();
    const category = String(parent?.entityCategory || '').trim();
    const holdingKind = String(parent?.holdingKind || '').trim();
    if (type === 'PIC' || type === 'HoldingPassive' || category === 'pic' ||
      (category === 'holding' && holdingKind === 'passive')) return [];
    const total = Math.round(children.reduce((sum, child) => sum + child.percent, 0) * 100) / 100;
    return total < 99 || total > 100 ? children.map(child => ({ ...child, total })) : [];
  });
}

export function highlightInvalidOwnershipPercentages(container, invalidEdges = []) {
  if (!container || !invalidEdges?.length) return;
  // Mermaid attaches its connection ID to each label. Match that ID, never the
  // percentage text: identical percentages can appear in unrelated branches.
  container.querySelectorAll('.edgeLabel [data-id]').forEach(label => {
    const id = label.getAttribute('data-id');
    const edge = invalidEdges.find(({ parentId, nodeId }) => {
      const prefix = `L_${parentId}_${nodeId}_`;
      return id.startsWith(prefix) && /^\d+$/.test(id.slice(prefix.length));
    });
    if (!edge || !label.textContent.includes('%')) return;
    label.classList.add('ownershipPercentageInvalid');
    label.querySelectorAll('span, p, div, text, tspan').forEach(text => {
      text.style.setProperty('color', '#9b525b', 'important');
      if (text.namespaceURI === 'http://www.w3.org/2000/svg') {
        text.style.setProperty('fill', '#9b525b', 'important');
      }
    });
    // Mermaid measures labels before the application's font styles apply. Centre
    // the frame on the visible text instead of the stale foreignObject size.
    let bounds = label.getBBox?.();
    const textRect = label.querySelector('foreignObject p, foreignObject span, text')?.getBoundingClientRect();
    const matrix = label.getScreenCTM?.()?.inverse();
    if (matrix && textRect?.width > 0 && textRect?.height > 0) {
      const localPoint = (x, y) => ({ x: matrix.a * x + matrix.c * y + matrix.e, y: matrix.b * x + matrix.d * y + matrix.f });
      const start = localPoint(textRect.left, textRect.top);
      const end = localPoint(textRect.right, textRect.bottom);
      bounds = { x: start.x, y: start.y, width: end.x - start.x, height: end.y - start.y };
    }
    if (bounds?.width > 0 && bounds?.height > 0) {
      const frame = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      frame.classList.add('ownershipPercentageFrame');
      frame.setAttribute('cx', bounds.x + bounds.width / 2);
      frame.setAttribute('cy', bounds.y + bounds.height / 2);
      frame.setAttribute('r', Math.max(bounds.width, bounds.height) / 2 + 5);
      frame.setAttribute('vector-effect', 'non-scaling-stroke');
      frame.setAttribute('aria-hidden', 'true');
      frame.style.setProperty('fill', '#f9eff0', 'important');
      frame.style.setProperty('stroke', 'none', 'important');
      frame.style.setProperty('stroke-width', '1px', 'important');
      frame.style.pointerEvents = 'none';
      label.prepend(frame);
      // The circle supplies one solid backdrop; avoid rectangular patches from
      // Mermaid's nested HTML label backgrounds covering it.
      label.querySelectorAll('foreignObject *').forEach(element => {
        element.style.setProperty('background', 'transparent', 'important');
      });
      label.querySelectorAll('rect').forEach(rect => {
        rect.style.setProperty('fill', 'transparent', 'important');
      });
    }
    const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
    title.textContent = `Ownership at this level totals ${edge.total}%. It must total 100%.`;
    label.appendChild(title);
  });
}
